/**
 * Interface Admin - Statistiques des Réservations (Simplifié)
 * Affiche uniquement le nombre de réservations sur une période personnalisable
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Collapse,
  TextField,
  Paper,
  IconButton,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  FilterList as FilterIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  AccountBalance as AccountBalanceIcon,
  Warning as WarningIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { useAuth } from '../../context/AuthContext';
import adminReservationService from '../../services/adminReservationService';
import { RESERVATION_STATUS_CONFIG } from '../../constants/reservationStatuses';
import AdminPaymentsByOwner from '../../components/admin/AdminPaymentsByOwner';
import AdminAlertsPage from './AdminAlertsPage';
import AdminReservationsManagementTab from '../../components/admin/AdminReservationsManagementTab';

const AdminReservations = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [statsData, setStatsData] = useState(null);
  const [error, setError] = useState(null);
  
  // État pour gérer les onglets
  const [currentTab, setCurrentTab] = useState(0);
  
  // États pour les filtres
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    startDate: dayjs().subtract(30, 'day'), // 30 jours par défaut
    endDate: dayjs()
  });

  // ✅ États pour les alertes
  const [alertsData, setAlertsData] = useState(null);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsError, setAlertsError] = useState(null);
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  // Configuration de dayjs en français
  dayjs.locale('fr');

  useEffect(() => {
    loadReservationStats();
  }, [filters]); // Recharger quand les filtres changent

  const loadReservationStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Prépare les paramètres de filtre pour l'API
      const filterParams = {
        startDate: filters.startDate ? filters.startDate.format('YYYY-MM-DD') : null,
        endDate: filters.endDate ? filters.endDate.format('YYYY-MM-DD') : null
      };
      
      console.log('🔍 Appel API avec filtres:', filterParams);
      
      // Charge les statistiques avec les filtres appliqués
      const response = await adminReservationService.getReservationsCount(filterParams);
      
      console.log('📊 Réponse API complète:', response);
      console.log('📈 Données reçues:', response.data);
      
      if (response.success) {
        console.log('✅ StatusBreakdown reçu:', response.data.statusBreakdown);
        setStatsData(response.data);
      } else {
        setError('Erreur lors du chargement des statistiques');
      }
      
    } catch (error) {
      console.error('❌ Error loading reservation stats:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      startDate: dayjs().subtract(30, 'day'),
      endDate: dayjs()
    });
  };

  const toggleFilter = () => {
    setFilterOpen(!filterOpen);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    
    // ✅ Charger les alertes quand on active l'onglet alertes
    if (newValue === 2 && !alertsData) {
      loadAlerts();
    }
  };

  // ✅ Fonction pour charger les alertes
  const loadAlerts = async () => {
    try {
      setAlertsLoading(true);
      setAlertsError(null);
      
      console.log('🚨 Loading reservation alerts...');
      
      const response = await adminReservationService.getAlerts({
        severity: selectedSeverity,
        limit: 50,
        acknowledged: false
      });

      if (response.success) {
        setAlertsData(response.data);
        console.log('✅ Alerts loaded:', response.data);
      } else {
        throw new Error(response.message || 'Erreur lors du chargement des alertes');
      }
    } catch (err) {
      console.error('❌ Error loading alerts:', err);
      setAlertsError(err.message || 'Erreur lors du chargement des alertes');
    } finally {
      setAlertsLoading(false);
    }
  };

  // ✅ Fonction pour traiter une alerte
  const handleAcknowledgeAlert = async (alertId) => {
    try {
      const response = await adminReservationService.acknowledgeAlert(alertId, {
        note: 'Alerte traitée par administrateur'
      });

      if (response.success) {
        // Recharger les alertes
        await loadAlerts();
        console.log('✅ Alert acknowledged:', alertId);
      }
    } catch (err) {
      console.error('❌ Error acknowledging alert:', err);
      setAlertsError(err.message || 'Erreur lors du traitement de l\'alerte');
    }
  };

  // ✅ Fonction pour changer le filtre de sévérité
  const handleSeverityChange = (event) => {
    setSelectedSeverity(event.target.value);
  };

  // ✅ Recharger les alertes quand le filtre de sévérité change
  useEffect(() => {
    if (currentTab === 2 && alertsData) {
      loadAlerts();
    }
  }, [selectedSeverity]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
      <Box sx={{ p: 3 }}>
        {/* En-tête avec onglets */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Administration des Réservations
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Gestion et analyse des réservations et paiements
          </Typography>
          
          {/* Système d'onglets */}
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange}
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              mb: 3
            }}
          >
            <Tab 
              icon={<AssessmentIcon />} 
              iconPosition="start"
              label="Statistiques des Réservations" 
              sx={{ textTransform: 'none', fontSize: '1rem' }}
            />
            <Tab 
              icon={<AccountBalanceIcon />} 
              iconPosition="start"
              label="Paiements par Propriétaire" 
              sx={{ textTransform: 'none', fontSize: '1rem' }}
            />
            <Tab 
              icon={<WarningIcon />} 
              iconPosition="start"
              label="Alertes Réservations" 
              sx={{ textTransform: 'none', fontSize: '1rem' }}
            />
            <Tab 
              icon={<NotificationsIcon />} 
              iconPosition="start"
              label="Alertes Avancées" 
              sx={{ textTransform: 'none', fontSize: '1rem' }}
            />
            <Tab 
              icon={<AssessmentIcon />} 
              iconPosition="start"
              label="Gestion Réservations" 
              sx={{ textTransform: 'none', fontSize: '1rem', fontWeight: 700, color: 'primary.main' }}
            />
          </Tabs>
        </Box>

        {/* Contenu du premier onglet - Statistiques des réservations */}
        {currentTab === 0 && (
          <Box>
            {/* Bouton filtre pour les statistiques */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                endIcon={filterOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={toggleFilter}
                sx={{ minWidth: 120 }}
              >
                Filtres
              </Button>
            </Box>

        {/* Panneau de filtres rétractable */}
        <Collapse in={filterOpen}>
          <Paper 
            sx={{ 
              mb: 3, 
              p: 3,
              backgroundColor: '#f8f9fa',
              border: '1px solid #e9ecef'
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Filtres de période
            </Typography>
            
            <Grid container spacing={3} alignItems="center">
              {/* Date de début */}
              <Grid item xs={12} sm={4}>
                <DatePicker
                  label="Date de début"
                  value={filters.startDate}
                  onChange={(newValue) => handleFilterChange('startDate', newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </Grid>
              
              {/* Date de fin */}
              <Grid item xs={12} sm={4}>
                <DatePicker
                  label="Date de fin"
                  value={filters.endDate}
                  onChange={(newValue) => handleFilterChange('endDate', newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </Grid>
              
              {/* Boutons d'action */}
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={resetFilters}
                    startIcon={<RefreshIcon />}
                  >
                    Réinitialiser
                  </Button>
                </Box>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 2 }} />
            
            {/* Résumé des filtres */}
            <Typography variant="body2" color="text.secondary">
              <strong>Période active :</strong> {' '}
              {filters.startDate?.format('DD/MM/YYYY') || 'Non définie'} - {' '}
              {filters.endDate?.format('DD/MM/YYYY') || 'Non définie'}
            </Typography>
          </Paper>
        </Collapse>

        {/* Gestion des erreurs */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Carte principale des statistiques */}
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white'
              }}
            >
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* En-tête de la carte */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <AnalyticsIcon sx={{ fontSize: 40, mr: 2, opacity: 0.8 }} />
                  <Box>
                    <Typography variant="h5" component="div">
                      Statistiques des Réservations
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Répartition détaillée par statut
                    </Typography>
                  </Box>
                </Box>
                
                {statsData ? (
                  <>
                    {/* Total global */}
                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                      <Typography variant="h1" component="div" sx={{ mb: 1, fontWeight: 'bold', fontSize: '3rem' }}>
                        {statsData.totalCount.toLocaleString('fr-FR')}
                      </Typography>
                      <Typography variant="h6" sx={{ opacity: 0.9 }}>
                        Réservations sur {statsData.period}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 2, backgroundColor: 'rgba(255,255,255,0.3)' }} />
                    
                    {/* Détail par statut */}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                        Répartition par statut
                      </Typography>
                      <Grid container spacing={2}>
                        {Object.entries(statsData.statusBreakdown || {}).map(([status, count]) => {
                          console.log(`🔍 Traitement statut: ${status} = ${count}`);
                          const config = RESERVATION_STATUS_CONFIG[status];
                          console.log(`🎨 Config trouvée pour ${status}:`, config);
                          
                          if (!config) {
                            console.log(`⚠️ Aucune config trouvée pour le statut: ${status}`);
                            return null;
                          }
                          
                          // Temporairement: afficher même les statuts avec 0 pour debug
                          // if (count === 0) {
                          //   console.log(`0️⃣ Count = 0 pour ${status}, masqué`);
                          //   return null;
                          // }
                          
                          const percentage = statsData.totalCount > 0 
                            ? ((count / statsData.totalCount) * 100).toFixed(1)
                            : 0;
                          
                          console.log(`✅ Affichage: ${status} - ${count} (${percentage}%)`);
                          
                          return (
                            <Grid item xs={12} sm={6} md={3} key={status}>
                              <Paper
                                sx={{
                                  p: 2,
                                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                  color: 'text.primary',
                                  border: `2px solid ${config.color}`,
                                  borderRadius: 2,
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  textAlign: 'center'
                                }}
                              >
                                <Typography sx={{ fontSize: '1.5rem', mb: 1 }}>
                                  {config.icon}
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 'bold', color: config.color, mb: 1 }}>
                                  {count}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                  {config.label}
                                </Typography>
                                <Chip
                                  label={`${percentage}%`}
                                  size="small"
                                  sx={{
                                    backgroundColor: config.bgColor,
                                    color: config.color,
                                    fontSize: '0.75rem'
                                  }}
                                />
                              </Paper>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                    
                    {/* Pied de carte */}
                    <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Période : Du {formatDate(statsData.startDate)} au {formatDate(statsData.endDate)}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1">
                      Aucune donnée disponible
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
          </Box>
        )}

        {/* Contenu du deuxième onglet - Paiements par propriétaire */}
        {currentTab === 1 && (
          <Box>
            <AdminPaymentsByOwner />
          </Box>
        )}

        {/* ✅ Contenu du troisième onglet - Alertes Réservations */}
        {currentTab === 2 && (
          <Box>
            {/* Header avec filtres */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" component="h2">
                🚨 Alertes de Réservations
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  select
                  size="small"
                  label="Sévérité"
                  value={selectedSeverity}
                  onChange={handleSeverityChange}
                  SelectProps={{
                    native: true,
                  }}
                  sx={{ minWidth: 120 }}
                >
                  <option value="all">Toutes</option>
                  <option value="critical">Critiques</option>
                  <option value="high">Hautes</option>
                  <option value="medium">Moyennes</option>
                  <option value="low">Basses</option>
                </TextField>
                
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadAlerts}
                  disabled={alertsLoading}
                >
                  Actualiser
                </Button>
              </Box>
            </Box>

            {/* Messages d'état */}
            {alertsError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {alertsError}
              </Alert>
            )}

            {/* Chargement */}
            {alertsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {/* Résumé des alertes */}
            {alertsData && !alertsLoading && (
              <>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="error.main">
                          {alertsData.summary.by_severity.critical}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Critiques
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="warning.main">
                          {alertsData.summary.by_severity.high}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Hautes
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="info.main">
                          {alertsData.summary.by_severity.medium}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Moyennes
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main">
                          {alertsData.summary.by_severity.low}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Basses
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Liste des alertes */}
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Alertes Détectées ({alertsData.summary.total_alerts})
                </Typography>

                {alertsData.alerts.length === 0 ? (
                  <Alert severity="success">
                    🎉 Aucune alerte détectée ! Toutes les réservations sont dans un état cohérent.
                  </Alert>
                ) : (
                  <Grid container spacing={2}>
                    {alertsData.alerts.map((alert) => (
                      <Grid item xs={12} key={alert.id}>
                        <Card 
                          sx={{ 
                            borderLeft: 4, 
                            borderColor: 
                              alert.severity === 'critical' ? 'error.main' :
                              alert.severity === 'high' ? 'warning.main' :
                              alert.severity === 'medium' ? 'info.main' : 'success.main'
                          }}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <Chip 
                                    label={alert.severity.toUpperCase()}
                                    size="small"
                                    color={
                                      alert.severity === 'critical' ? 'error' :
                                      alert.severity === 'high' ? 'warning' :
                                      alert.severity === 'medium' ? 'info' : 'success'
                                    }
                                    sx={{ mr: 1 }}
                                  />
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    {alert.title}
                                  </Typography>
                                </Box>
                                
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  {alert.description}
                                </Typography>
                                
                                <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="caption">
                                    <strong>Réservation:</strong> {alert.reservation_id}
                                  </Typography>
                                  <Typography variant="caption">
                                    <strong>Statut actuel:</strong> {alert.current_status}
                                  </Typography>
                                  {alert.expected_status && (
                                    <Typography variant="caption">
                                      <strong>Statut attendu:</strong> {alert.expected_status}
                                    </Typography>
                                  )}
                                  {alert.payment_context && (
                                    <Chip 
                                      label={
                                        alert.payment_context === 'online_paid' ? '💳 Payé en ligne' :
                                        alert.payment_context === 'cash_planned' ? '💵 Cash prévu' :
                                        alert.payment_context === 'cash_received' ? '💵 Cash reçu' :
                                        alert.payment_context === 'online_pending' ? '⏳ Paiement en attente' :
                                        '❓ Paiement inconnu'
                                      }
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  )}
                                  {alert.responsibility && (
                                    <Chip 
                                      label={`Responsable: ${
                                        alert.responsibility === 'OWNER_CRITICAL' ? '👨‍💼 Propriétaire (Urgent)' :
                                        alert.responsibility === 'OWNER' ? '👨‍💼 Propriétaire' :
                                        alert.responsibility === 'RENTER' ? '👤 Locataire' :
                                        alert.responsibility === 'BOTH_PARTIES' ? '👥 Les deux parties' :
                                        alert.responsibility === 'SYSTEM' ? '🤖 Système' :
                                        alert.responsibility
                                      }`}
                                      size="small"
                                      color={
                                        alert.responsibility === 'OWNER_CRITICAL' ? 'error' :
                                        alert.responsibility === 'SYSTEM' ? 'success' :
                                        'info'
                                      }
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Box>
                                
                                {alert.suggested_action && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                      🎯 Action suggérée: {
                                        alert.suggested_action === 'CONTACT_OWNER_URGENT' ? 'Contacter le propriétaire en URGENCE' :
                                        alert.suggested_action === 'CONTACT_OWNER' ? 'Contacter le propriétaire' :
                                        alert.suggested_action === 'CONTACT_RENTER' ? 'Contacter le locataire' :
                                        alert.suggested_action === 'VERIFY_BOTH_PRESENCE' ? 'Vérifier la présence des deux parties' :
                                        alert.suggested_action === 'APPLY_LATE_FEES_AUTO' ? 'Appliquer des frais de retard automatiquement' :
                                        alert.suggested_action === 'MANUAL_FEE_COLLECTION' ? 'Collecter des frais manuellement' :
                                        alert.suggested_action === 'AUTO_EXPIRE' ? 'Expiration automatique recommandée' :
                                        alert.suggested_action === 'SUGGEST_FORCE_COMPLETE' ? 'Forcer la completion suggérée' :
                                        alert.suggested_action
                                      }
                                    </Typography>
                                  </Box>
                                )}
                                
                                {alert.renter && (
                                  <Typography variant="caption" display="block">
                                    <strong>Locataire:</strong> {alert.renter.name} ({alert.renter.email})
                                  </Typography>
                                )}
                                
                                {alert.listing && (
                                  <Typography variant="caption" display="block">
                                    <strong>Bien:</strong> {alert.listing.title}
                                  </Typography>
                                )}
                              </Box>
                              
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleAcknowledgeAlert(alert.id)}
                                sx={{ ml: 2 }}
                              >
                                Traiter
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            )}
          </Box>
        )}

        {/* Contenu du quatrième onglet - Alertes Avancées */}
        {currentTab === 3 && (
          <Box>
            <AdminAlertsPage />
          </Box>
        )}

        {/* Contenu du cinquième onglet - Gestion complète des réservations */}
        {currentTab === 4 && (
          <Box>
            <AdminReservationsManagementTab />
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default AdminReservations;